import type {
  DataTableProviderFilterItem,
  DataTableProviderResolverParameters
} from '@/components/common/DataTableProvider/types';
import type {
  CampaignInterface,
  CampaignMetricsListSummaryInterface,
  CampaignTypeInterface
} from '@/interfaces/Campaign.interface';
import type { ListDataParams } from '@/api-client/types';
import {
  TagModes,
  type TagModesType
} from 'stubben-edge-unified-frontend-react-components';
import { isMessagingCampaignType } from './campaignTypeUtils';
import {
  formatCampaignLaunchedAt,
  formatCampaignScheduledStartAt,
  resolveCampaignDisplayTimezone
} from '../CampaignView/utils/campaignViewDetails';

export const campaignListStatusTagMode: Record<string, TagModesType> = {
  draft: TagModes.Disabled,
  scheduled: TagModes.Normal,
  pending: TagModes.Warning,
  active: TagModes.Success,
  completed: TagModes.Success,
  paused: TagModes.Warning
};

/** @deprecated Use campaignListStatusTagMode */
export const campaignListStatusColor = campaignListStatusTagMode;

export function formatCampaignStatusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function isCampaignDraftDeletable(status: string): boolean {
  return status === 'draft';
}

function formatCampaignListScheduledStart(
  campaign: Pick<
    CampaignInterface,
    'start_date' | 'send_time' | 'timezone'
  >
): string {
  return formatCampaignScheduledStartAt(
    campaign.start_date,
    campaign.send_time,
    resolveCampaignDisplayTimezone(campaign as CampaignInterface)
  );
}

export function formatCampaignListStartDate(
  campaign: Pick<
    CampaignInterface,
    'status' | 'start_date' | 'send_time' | 'timezone' | 'launched_at'
  >
): string {
  if (campaign.status === 'draft') {
    return '—';
  }

  if (campaign.status === 'scheduled' && campaign.start_date) {
    return formatCampaignListScheduledStart(campaign);
  }

  const timeZone = resolveCampaignDisplayTimezone(campaign);

  if (campaign.launched_at) {
    return formatCampaignLaunchedAt(campaign.launched_at, timeZone);
  }

  if (campaign.start_date) {
    return formatCampaignListScheduledStart(campaign);
  }

  return '—';
}

export function formatCampaignListMetricRate(rate: number): string {
  return `${rate}%`;
}

export function resolveCampaignListOpenedMetric(
  campaign: Pick<CampaignInterface, 'campaign_type' | 'metrics_summary'>
): Pick<CampaignMetricsListSummaryInterface, 'openedCount' | 'openedRate'> | null {
  const summary = campaign.metrics_summary;
  if (!summary) {
    return null;
  }

  if (
    isMessagingCampaignType(campaign.campaign_type) &&
    summary.readCount != null
  ) {
    return {
      openedCount: summary.readCount,
      openedRate:
        summary.deliveredCount > 0
          ? Math.round((summary.readCount / summary.deliveredCount) * 10000) /
            100
          : 0
    };
  }

  return {
    openedCount: summary.openedCount,
    openedRate: summary.openedRate
  };
}

export function buildCampaignListFilters(
  types: CampaignTypeInterface[]
): DataTableProviderFilterItem[] {
  return [
    {
      label: 'Status',
      placeholder: 'All statuses',
      filterKey: 'status',
      type: 'select',
      fullWidth: true,
      selectOptions: [
        { value: 'draft', label: 'Draft' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'pending', label: 'Pending' },
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'paused', label: 'Paused' }
      ]
    },
    {
      label: 'Campaign type',
      placeholder: 'All types',
      filterKey: 'campaign_type_id',
      type: 'select',
      fullWidth: true,
      selectOptions: types.map((type) => ({
        value: String(type.id),
        label: type.name
      }))
    }
  ];
}

export function mapCampaignListParams({
  searchTerm: _searchTerm,
  sort,
  filters,
  page,
  perPage
}: DataTableProviderResolverParameters): ListDataParams {
  const mappedFilters: Record<string, unknown> = {};

  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      if (value === '' || value === null || value === undefined) continue;

      if (key === 'campaign_type_id') {
        const id = Number(value);
        if (Number.isFinite(id)) {
          mappedFilters.campaign_type_id = id;
        }
        continue;
      }

      mappedFilters[key] = value;
    }
  }

  return {
    page,
    perPage,
    sort: sort ?? undefined,
    filters:
      Object.keys(mappedFilters).length > 0 ? mappedFilters : undefined
  };
}
